import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useGetBrandsListQuery } from 'entities/Brand';
import { useGetAllOfferListQuery } from 'entities/Offer';
import { IArbitrageUrl, IBloggerUrl, useGetSourceListQuery } from 'entities/Source';
import { EFilterOptions, EFilters, META_REQUEST_LIMIT } from 'features/Filters';
import { EPaginationParams } from 'features/Pagination';
import { useGetAppConfigQuery } from 'shared/api/commonApi';
import { useCommonSlice } from 'shared/hooks';
import { EDefaultOptions } from 'shared/typings';

export const useFilters = () => {
	const { clearInfinityScroll } = useCommonSlice();
	const [searchParams, setSearchParams] = useSearchParams();
	const { pathname } = useLocation();

	const { data: appConfig } = useGetAppConfigQuery();

	const { data: sourceList, isFetching: sourcesIsFetching } = useGetSourceListQuery(`limit=${META_REQUEST_LIMIT}`);
	const sourcesOptions = useMemo(
		() =>
			sourceList?.results?.map(({ name, uuid }) => ({
				value: uuid,
				label: name,
			})) || [],
		[sourceList],
	);

	const platformTypesMap = sourceList?.results?.reduce(
		(acc, source) => {
			return {
				...acc,
				[source.uuid]: source?.influencer_data?.urls || source?.arbitrage_data?.urls,
			};
		},
		{} as Record<string, IBloggerUrl[] | IArbitrageUrl[]>,
	);

	const { data: brandsList, isFetching: brandsIsFetching } = useGetBrandsListQuery(`limit=${META_REQUEST_LIMIT}`);
	const brandsOptions = useMemo(
		() =>
			brandsList?.results?.map(({ uuid, name }) => ({
				value: uuid,
				label: name,
			})) || [],
		[brandsList],
	);

	const { data: offersList, isFetching: offersIsFetching } = useGetAllOfferListQuery(`limit=${META_REQUEST_LIMIT}`);
	const offersOptions = useMemo(
		() =>
			offersList?.results?.map(({ uuid, name }) => ({
				value: uuid,
				label: name,
			})) || [],
		[offersList],
	);

	const setFiltersToStorage = () => {
		if (appConfig?.user?.user_id) {
			const filters = JSON.parse(localStorage.getItem(`filters_${appConfig.user.user_id}`) || '{}');
			localStorage.setItem(
				`filters_${appConfig.user.user_id}`,
				JSON.stringify({ ...filters, [pathname]: searchParams.toString() }),
			);
		}
	};

	return {
		sourcesOptions,
		brandsOptions,
		offersOptions,

		sourcesIsFetching,
		brandsIsFetching,
		offersIsFetching,

		platformTypesMap,

		getFiltersCount: (filters?: EFilters[]) => {
			return Object.values(EFilters).reduce((acc, key) => {
				const param = searchParams.getAll(key);

				if (!param.length || param[0] === EDefaultOptions.ALL || (filters && !filters.includes(key))) {
					return acc;
				}

				return acc + 1;
			}, 0);
		},

		onFilterChange: (name: EFilters, value?: string | string[]) => {
			if (!value || value === EFilterOptions.ALL) {
				searchParams.delete(name);
				setSearchParams(searchParams);
				setFiltersToStorage();
				return;
			}

			if (Array.isArray(value)) {
				searchParams.delete(name);

				value.forEach((item) => {
					item && item !== EFilterOptions.ALL && searchParams.append(name, item);
				});
			} else {
				searchParams.set(name, value);
			}

			searchParams.delete(EPaginationParams.OFFSET);
			setSearchParams(searchParams);
			setFiltersToStorage();
		},

		getFilterValue: (name: EFilters) => {
			return searchParams.get(name) || null;
		},

		getALLFilterValues: (
			name: EFilters,
			options?: {
				value: string;
				label: string;
			}[],
		) => {
			return options
				? searchParams.getAll(name).filter((item) => options?.some((option) => option.value === item)) || null
				: searchParams.getAll(name) || null;
		},

		clearFilters: (filters?: EFilters[]) => {
			if (filters) {
				filters.forEach((filter) => {
					searchParams.delete(filter);
				});
			} else {
				for (const key in EFilters) {
					searchParams.delete(EFilters[key as keyof typeof EFilters]);
				}
			}

			searchParams.delete(EPaginationParams.OFFSET);
			clearInfinityScroll();

			setSearchParams(searchParams);
			setFiltersToStorage();
		},
	};
};
